// src/pages/VerifyOtp.js
import React, { useState, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { postData } from "../services/request";
import logo from "../assets/logo/logo3.png";

const VerifyOtp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(location.state?.email || "");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const inputRefs = useRef([]);

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value.replace(/[^0-9]/g, "").slice(0, 1);
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle Backspace
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle Paste (Saare 4 digits paste honge)
  const handlePaste = (e) => {
    e.preventDefault(); // Ab ye theek chalega
    const pasted = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, 4);
    if (pasted) {
      const newOtp = pasted.split("");
      // Fill the boxes
      const updatedOtp = ["", "", "", ""];
      newOtp.forEach((digit, i) => {
        if (i < 4) updatedOtp[i] = digit;
      });
      setOtp(updatedOtp);
      inputRefs.current[Math.min(pasted.length - 1, 3)]?.focus();
    }
  };

  const handleVerify = async () => {
    const token = otp.join("");
    if (!email) {
      setSnackbar({
        open: true,
        message: "Email is missing",
        severity: "error",
      });
      return;
    }
    if (token.length !== 4) {
      setSnackbar({
        open: true,
        message: "Please enter complete 4-digit code",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: email,
        token: token,
      };

      const response = await postData("/api/auth/token/verify", payload);

      if (response.status === true) {
        localStorage.setItem("accessToken", response.accessToken);
        localStorage.setItem("userData", JSON.stringify(response.data));
        if (Array.isArray(response.notifications)) {
          localStorage.setItem(
            "notifications",
            JSON.stringify(response.notifications),
          );
        }
        setSnackbar({
          open: true,
          message: "Code Verified Successfully! Redirecting...",
          severity: "success",
        });
        setTimeout(() => navigate("/nin-verification"), 1500);
      } else {
        setSnackbar({
          open: true,
          message: response.message || "Invalid code. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setSnackbar({
      open: true,
      message: "New code sent to your email!",
      severity: "success",
    });
  };

  return (
    <Box
      sx={{
        minHeight: "88vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f0ff 0%, #ffffff 100%)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: { xs: "90%", sm: 450 },
          p: { xs: 3, sm: 5 },
          borderRadius: "24px",
          background: "#fff",
          boxShadow: "0px 20px 60px rgba(82, 5, 123, 0.15)",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <img src={logo} alt="Dajoo" style={{ width: "120px" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ color: "#52057b", fontWeight: 800, mb: 1 }}
          >
            Enter 4-Digit Code
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#666", fontSize: "0.95rem" }}
          >
            We've sent a verification code to your email
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#52057b", fontWeight: 600, mt: 1 }}
          >
            {email || "example@gmail.com"}
          </Typography>
        </Box>

        {/* OTP Inputs */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, my: 4 }}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              // Paste event ko TextField ke inputProps mein lagaya
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  padding: "0",
                },
                onPaste: handlePaste, // Yahan paste handle ho raha hai
              }}
              sx={{
                width: "60px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  height: "70px",
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                  "&.Mui-focused": {
                    boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
                    borderColor: "#52057b",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e0e0e0",
                },
              }}
            />
          ))}
        </Box>

        {/* Verify Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleVerify}
          disabled={loading}
          sx={{
            bgcolor: "#52057b",
            color: "#fff",
            py: 2,
            borderRadius: "12px",
            fontWeight: "bold",
            fontSize: "1.1rem",
            textTransform: "none",
            boxShadow: "0px 10px 25px rgba(82, 5, 123, 0.3)",
            "&:hover": {
              bgcolor: "#3e055e",
              boxShadow: "0px 15px 35px rgba(82, 5, 123, 0.4)",
              transform: "translateY(-2px)",
            },
            "&.Mui-disabled": {
              bgcolor: "#e0e0e0",
              color: "#9e9e9e",
              boxShadow: "none",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Verify Code"
          )}
        </Button>

        {/* Resend Code */}
        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 3, color: "#666" }}
        >
          Didn't receive the code?{" "}
          <span
            onClick={handleResend}
            style={{ color: "#52057b", fontWeight: "bold", cursor: "pointer" }}
          >
            Resend Code
          </span>
        </Typography>

        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 2, color: "#666" }}
        >
          <a
            href="/"
            style={{
              color: "#52057b",
              textDecoration: "none",
              fontWeight: "bold",
            }}
          >
            Back to Login
          </a>
        </Typography>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{ width: "100%", borderRadius: "12px" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default VerifyOtp;
